import trimStart from 'lodash/trimStart';

/**
 * Append a specified suffix
 */
type AddSuffix = (base: number | string) => string;

export function setSuffix(suffix: string): AddSuffix {
  return function addSuffix(base) {
    return `${base}${suffix}`;
  };
}

export const toPct = setSuffix('%');
export const toPx = setSuffix('px');
export const toRem = setSuffix('rem');
export const addS = setSuffix('s');

/**
 * Converts a given px size to its REM equivalent
 * 14 is the default baseUnit due to its use as the base
 * fontSize in the application
 */
export const pxToRem = (px: number, baseUnit = 14): string => toRem(px / baseUnit);

export const fixHex = (hex = '') => {
  if (hex.length === 7) return hex;

  // Fix for 3 letter hex - EG: #FFF
  if (hex.length === 4) {
    const hexBase = trimStart(hex, '#');
    return `#${hexBase}${hexBase}`;
  }

  throw new Error(`Invalid hex: ${hex}`);
};

/**
 * Converts a hex code to either rgb or rgba
 */
export const hexToRgba = (hex: string, opacity?: number) => {
  const fixedHex = fixHex(hex);
  const r = parseInt(fixedHex.slice(1, 3), 16);
  const g = parseInt(fixedHex.slice(3, 5), 16);
  const b = parseInt(fixedHex.slice(5, 7), 16);
  const isOpacityValid = typeof opacity === 'number' && opacity >= 0 && opacity <= 1;

  return `rgba(${r}, ${g}, ${b}, ${isOpacityValid ? opacity : 1})`;
};

/**
 * Converts a given value to a percentage
 * @param value The value to convert
 * @param region The region to use for formatting.  Defaults to the user's locale.
 * @param maximumFractionDigits The maximum number of digits to display after the decimal point.
 * @returns The value formatted as a percentage
 * @example
 * convertToPercent(0.5) // '50%'
 * convertToPercent(0.123456) // '12.35%', if the user's locale is 'en-US'
 * convertToPercent('0.123456', 'en-DE') // '12,35 %'
 */
export const convertToPercent = (
  value: number | string | undefined,
  maximumFractionDigits?: number,
  region?: string,
): string =>
  new Intl.NumberFormat(region, {
    style: 'percent',
    maximumFractionDigits,
  }).format(Number(value));

/**
 * Format a number as a currency when the currency type is unknown.
 * It will format the number with 2 trailing decimal places.
 * @param value The value to convert
 * @param region The region to use for formatting.  Defaults to the user's locale.
 * @returns The value formatted as a local number with 2 trailing decimal places
 * @example
 * convertToUnknownCurrency(50) // '50.00'
 * convertToUnknownCurrency(123456.78) // '123,456.78', if the user's locale is 'en-US'
 * convertToUnknownCurrency(123456.78, 'en-DE') // '123.456,78'
 */
export const convertToUnknownCurrency = (value: number | string | undefined, region?: string) =>
  new Intl.NumberFormat(region, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
